/**
 * Nut Sheet Types Configuration Tab.
 *
 * Lets Admins manage the per-location Type drop-down lists that drive the
 * Nut Sheet page's editable rows. One collapsible section per location with
 * two editable lists (NEW / USED). Each row is a (Code, Label) pair. Saves
 * the location's entire list in one PUT (locked-step with the DDB record
 * shape — there's no per-row endpoint).
 */

import React, { useMemo, useState } from 'react';
import { CheckIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  LocationScheduleConfig,
  NutSheetTypeDef,
  NutSheetTypesConfig,
  deleteNutSheetTypes,
  saveNutSheetTypes,
} from '../../services/api';
import { INDIVIDUAL_LOCATIONS } from '../LocationDropdown';

interface NutSheetTypesTabProps {
  canEdit: boolean;
  locationSchedules: LocationScheduleConfig[];
  nutSheetTypes: NutSheetTypesConfig[];
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
  refresh: () => Promise<void>;
}

interface LocationEditState {
  newTypes: NutSheetTypeDef[];
  usedTypes: NutSheetTypeDef[];
}

const SUPPORTED_LOCATION_IDS = INDIVIDUAL_LOCATIONS
  .map((l) => l.id)
  .filter((id) => id !== 12 && id !== 44);

export const NutSheetTypesTab: React.FC<NutSheetTypesTabProps> = ({
  canEdit,
  locationSchedules,
  nutSheetTypes,
  onSuccess,
  onError,
  refresh,
}) => {
  const [expanded, setExpanded] = useState<number | null>(null);
  const [editing, setEditing] = useState<Record<number, LocationEditState>>({});
  const [saving, setSaving] = useState<Record<number, boolean>>({});

  const indexed = useMemo(() => {
    const m = new Map<number, NutSheetTypesConfig>();
    for (const t of nutSheetTypes) m.set(t.locationId, t);
    return m;
  }, [nutSheetTypes]);

  const locations = useMemo(() => {
    return SUPPORTED_LOCATION_IDS.map((id) => {
      const sched = locationSchedules.find((l) => l.locationId === id);
      const fallbackName =
        INDIVIDUAL_LOCATIONS.find((l) => l.id === id)?.name ?? `Location ${id}`;
      return {
        locationId: id,
        locationName: sched?.locationName ?? fallbackName,
      };
    });
  }, [locationSchedules]);

  const getCurrentEdit = (locationId: number): LocationEditState => {
    if (editing[locationId]) return editing[locationId];
    const existing = indexed.get(locationId);
    return {
      newTypes: existing?.newTypes ?? [],
      usedTypes: existing?.usedTypes ?? [],
    };
  };

  const startEditing = (locationId: number) => {
    setEditing((prev) => ({ ...prev, [locationId]: getCurrentEdit(locationId) }));
  };

  const cancelEditing = (locationId: number) => {
    setEditing((prev) => {
      const next = { ...prev };
      delete next[locationId];
      return next;
    });
  };

  const updateTypeRow = (
    locationId: number,
    listKey: 'newTypes' | 'usedTypes',
    index: number,
    patch: Partial<NutSheetTypeDef>,
  ) => {
    setEditing((prev) => {
      const current = prev[locationId] ?? getCurrentEdit(locationId);
      const updated = current[listKey].map((row, i) =>
        i === index ? { ...row, ...patch } : row,
      );
      return { ...prev, [locationId]: { ...current, [listKey]: updated } };
    });
  };

  const addTypeRow = (locationId: number, listKey: 'newTypes' | 'usedTypes') => {
    setEditing((prev) => {
      const current = prev[locationId] ?? getCurrentEdit(locationId);
      return {
        ...prev,
        [locationId]: {
          ...current,
          [listKey]: [...current[listKey], { code: '', label: '' }],
        },
      };
    });
  };

  const removeTypeRow = (
    locationId: number,
    listKey: 'newTypes' | 'usedTypes',
    index: number,
  ) => {
    setEditing((prev) => {
      const current = prev[locationId] ?? getCurrentEdit(locationId);
      return {
        ...prev,
        [locationId]: {
          ...current,
          [listKey]: current[listKey].filter((_, i) => i !== index),
        },
      };
    });
  };

  const handleSave = async (locationId: number) => {
    const draft = editing[locationId];
    if (!draft) return;
    const newTypes = draft.newTypes
      .map((t) => ({ code: t.code.trim(), label: t.label.trim() }))
      .filter((t) => t.code.length > 0 && t.label.length > 0);
    const usedTypes = draft.usedTypes
      .map((t) => ({ code: t.code.trim(), label: t.label.trim() }))
      .filter((t) => t.code.length > 0 && t.label.length > 0);

    setSaving((prev) => ({ ...prev, [locationId]: true }));
    const res = await saveNutSheetTypes({
      type: 'NUT_SHEET_TYPES',
      locationId,
      newTypes,
      usedTypes,
    });
    setSaving((prev) => {
      const next = { ...prev };
      delete next[locationId];
      return next;
    });

    if (res.success) {
      await refresh();
      cancelEditing(locationId);
      onSuccess(`Nut Sheet types saved for location ${locationId}`);
    } else {
      onError(res.error || 'Failed to save Nut Sheet types');
    }
  };

  const handleDelete = async (locationId: number) => {
    if (!window.confirm(`Delete the Nut Sheet Type lists for location ${locationId}? This cannot be undone.`)) {
      return;
    }
    setSaving((prev) => ({ ...prev, [locationId]: true }));
    const res = await deleteNutSheetTypes(locationId);
    setSaving((prev) => {
      const next = { ...prev };
      delete next[locationId];
      return next;
    });
    if (res.success) {
      await refresh();
      cancelEditing(locationId);
      onSuccess(`Nut Sheet types deleted for location ${locationId}`);
    } else {
      onError(res.error || 'Failed to delete Nut Sheet types');
    }
  };

  return (
    <div>
      <p className="text-sm text-gray-600 mb-4">
        Manage the per-location <strong>Type</strong> drop-down lists that
        drive the Nut Sheet page. NEW types feed the New Vehicle Summary;
        USED types feed the Used Vehicle Summary.
      </p>

      <div className="space-y-3">
        {locations.map((loc) => {
          const isExpanded = expanded === loc.locationId;
          const isEditing = Boolean(editing[loc.locationId]);
          const existing = indexed.get(loc.locationId);
          const draft = getCurrentEdit(loc.locationId);
          const newCount = (isEditing ? draft.newTypes : existing?.newTypes ?? []).length;
          const usedCount = (isEditing ? draft.usedTypes : existing?.usedTypes ?? []).length;
          const isSaving = !!saving[loc.locationId];

          return (
            <div key={loc.locationId} className="border border-gray-200 rounded-lg">
              <button
                onClick={() => setExpanded(isExpanded ? null : loc.locationId)}
                className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 rounded-t-lg"
              >
                <div className="flex items-center space-x-3">
                  <span className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                  <div className="text-left">
                    <div className="font-medium text-gray-900">
                      {loc.locationName} <span className="text-xs text-gray-500">(#{loc.locationId})</span>
                    </div>
                    <div className="text-sm text-gray-500">
                      {existing
                        ? `${existing.newTypes.length} NEW + ${existing.usedTypes.length} USED types`
                        : 'No types saved yet'}
                    </div>
                  </div>
                </div>
                {existing && (
                  <span className="inline-flex items-center text-xs text-green-700 bg-green-50 ring-1 ring-green-200 px-2 py-0.5 rounded">
                    <CheckIcon className="h-3 w-3 mr-1" />
                    Configured
                  </span>
                )}
              </button>

              {isExpanded && (
                <div className="p-4">
                  {!isEditing && (
                    <div className="flex justify-end gap-2 mb-3">
                      {canEdit && (
                        <button
                          onClick={() => startEditing(loc.locationId)}
                          className="px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
                        >
                          Edit
                        </button>
                      )}
                      {canEdit && existing && (
                        <button
                          onClick={() => handleDelete(loc.locationId)}
                          disabled={isSaving}
                          className="px-3 py-1 text-xs font-medium text-white bg-rose-600 hover:bg-rose-700 rounded disabled:opacity-50"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(['newTypes', 'usedTypes'] as const).map((listKey) => {
                      const rows = isEditing ? draft[listKey] : existing?.[listKey] ?? [];
                      const title = listKey === 'newTypes' ? 'NEW Types' : 'USED Types';
                      return (
                        <div key={listKey} className="border border-gray-200 rounded p-3">
                          <h4 className="text-sm font-semibold text-gray-900 mb-2">
                            {title} ({rows.length})
                          </h4>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b border-gray-200 text-left">
                                <th className="px-1 py-1 font-medium text-gray-600 w-20">Code</th>
                                <th className="px-1 py-1 font-medium text-gray-600">Label</th>
                                {isEditing && <th className="w-8" />}
                              </tr>
                            </thead>
                            <tbody>
                              {rows.length === 0 && (
                                <tr>
                                  <td colSpan={isEditing ? 3 : 2} className="px-1 py-2 text-center text-gray-400 italic">
                                    No types defined.
                                  </td>
                                </tr>
                              )}
                              {rows.map((t, i) => (
                                <tr key={i} className="border-b border-gray-100">
                                  <td className="px-1 py-1">
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        value={t.code}
                                        onChange={(e) => updateTypeRow(loc.locationId, listKey, i, { code: e.target.value })}
                                        className="w-full rounded border-gray-300 text-xs"
                                      />
                                    ) : (
                                      <span className="font-mono">{t.code}</span>
                                    )}
                                  </td>
                                  <td className="px-1 py-1">
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        value={t.label}
                                        onChange={(e) => updateTypeRow(loc.locationId, listKey, i, { label: e.target.value })}
                                        className="w-full rounded border-gray-300 text-xs"
                                      />
                                    ) : (
                                      <span>{t.label}</span>
                                    )}
                                  </td>
                                  {isEditing && (
                                    <td className="px-1 py-1">
                                      <button
                                        onClick={() => removeTypeRow(loc.locationId, listKey, i)}
                                        className="text-rose-600 hover:text-rose-800"
                                      >
                                        <TrashIcon className="h-3.5 w-3.5" />
                                      </button>
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {isEditing && (
                            <button
                              onClick={() => addTypeRow(loc.locationId, listKey)}
                              className="mt-2 inline-flex items-center text-xs text-blue-600 hover:text-blue-800"
                            >
                              <PlusIcon className="h-3 w-3 mr-1" />
                              Add {listKey === 'newTypes' ? 'NEW' : 'USED'} type
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {isEditing && (
                    <div className="mt-4 flex justify-end gap-2">
                      <button
                        onClick={() => cancelEditing(loc.locationId)}
                        disabled={isSaving}
                        className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSave(loc.locationId)}
                        disabled={isSaving}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50"
                      >
                        {isSaving ? 'Saving…' : `Save (${newCount + usedCount})`}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
